import React, { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import styled, { css } from "styled-components";
import useHistory from "routing/useHistory";
import CollectionDetails from "collections/components/CollectionDetails";
import { Button, ButtonToolbar, Row, Alert } from "components/bootstrap";
import { DocumentTitle, Icon, IfPermitted, PageHeader, ShareButton, Spinner } from "components/common";
import useParams from "routing/useParams";
import useCollection from "collections/hooks/useCollection";
import EntityShareModal from "components/permissions/EntityShareModal";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import UpdateCollectionButton from "collections/components/UpdateCollectionButton";
import { useTranslation } from "react-i18next";
import CollectionsRoutes from "../Routes";
const HeaderTitle = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.sm};
    margin-bottom: ${theme.spacings.xs};
  `
);
const StyledInfoList = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xs};
    margin-bottom: ${theme.spacings.xs};
    margin-left: ${theme.spacings.xs};
    list-style-type: none;
  `
);
const CollectionDetailsPage = () => {
  const { t } = useTranslation("enterprise_collections");
  const queryClient = useQueryClient();
  const { push } = useHistory();
  const params = useParams();
  const [showCollectionShareModal, setShowCollectionShareModal] = useState(false);
  const toggleCollectionShareModal = () => setShowCollectionShareModal(!showCollectionShareModal);
  const { data: collection, isLoading } = useCollection(params.collectionId, { enabled: !!params.collectionId });
  const collectionTitle = (collection == null ? void 0 : collection.title) ? t("details_page.title_with_name", { title: collection.title }) : t("details_page.title");
  const refetchCollection = useCallback(
    () => queryClient.invalidateQueries({
      queryKey: [COLLECTIONS_QUERY_KEY, collection == null ? void 0 : collection.id]
    }),
    [queryClient, collection == null ? void 0 : collection.id]
  );
  const handleShareClose = () => {
    refetchCollection();
    toggleCollectionShareModal();
  };
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: collectionTitle }, /* @__PURE__ */ React.createElement(
    PageHeader,
    {
      title: /* @__PURE__ */ React.createElement(HeaderTitle, null, /* @__PURE__ */ React.createElement(Button, { onClick: () => push(CollectionsRoutes.LIST) }, /* @__PURE__ */ React.createElement(Icon, { name: "arrow_left_alt", size: "sm" }), " ", t("details_page.back")), collectionTitle),
      topActions: collection && /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `collections:edit:${collection.id}` }, /* @__PURE__ */ React.createElement(UpdateCollectionButton, { editCollection: collection, buttonText: t("shared.edit") })), /* @__PURE__ */ React.createElement(
        ShareButton,
        {
          entityId: collection.id,
          entityType: "collection",
          onClick: toggleCollectionShareModal,
          title: t("shared.manage")
        }
      ))
    },
    (collection == null ? void 0 : collection.description) ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", null, collection == null ? void 0 : collection.description), " ", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info", title: t("details_page.page_information") }, /* @__PURE__ */ React.createElement(StyledInfoList, null, /* @__PURE__ */ React.createElement("li", null, t("details_page.info_content")), /* @__PURE__ */ React.createElement("li", null, t("details_page.info_sharing")), /* @__PURE__ */ React.createElement("li", null, t("details_page.info_modify"))))) : /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(StyledInfoList, null, /* @__PURE__ */ React.createElement("li", null, t("details_page.info_content")), /* @__PURE__ */ React.createElement("li", null, t("details_page.info_sharing")), /* @__PURE__ */ React.createElement("li", null, t("details_page.info_modify"))))
  ), isLoading && /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Spinner, { text: t("details_page.loading") })), collection && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(CollectionDetails, { collection }), showCollectionShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: collection.id,
      entityType: "collection",
      entityTitle: collection.title,
      description: t("shared.share_collection_description"),
      onClose: handleShareClose
    }
  )));
};
export default CollectionDetailsPage;
